import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useFetch } from "../api";
import { getAdmin } from "../auth";
import Icon from "../components/icons";
import { Alerts, Avatar, Badge, EmptyRow, Loading, Page, UserLink } from "../components/ui";
import { label, money, when } from "../format";

function CountUp({ value }) {
  const target = Number(value);
  const valid = value !== null && value !== undefined && value !== "" && Number.isFinite(target);
  const [shown, setShown] = useState(valid ? 0 : value);

  useEffect(() => {
    if (!valid) {
      setShown(value);
      return undefined;
    }
    const still = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (still || target === 0) {
      setShown(target);
      return undefined;
    }
    let frame;
    const start = performance.now();
    const duration = 1100;
    function tick(now) {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setShown(Math.round(target * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    }
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, target, valid]);

  return <>{shown}</>;
}

function Stat({ to, icon, tone, label: caption, value, meta }) {
  const body = (
    <>
      <div className="stat-top">
        <span className={"stat-icon tone-" + tone}><Icon name={icon} /></span>
        {to ? <Icon name="arrow" className="stat-go" /> : null}
      </div>
      <p className="stat-label">{caption}</p>
      <p className="stat-value"><CountUp value={value} /></p>
      {meta ? <p className="stat-meta">{meta}</p> : null}
    </>
  );
  if (to) return <Link className="stat" to={to}>{body}</Link>;
  return <article className="stat">{body}</article>;
}

function Card({ title, text, to, children }) {
  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2>{title}</h2>
          {text ? <p>{text}</p> : null}
        </div>
        {to ? <Link className="card-link" to={to}>View all <Icon name="arrow" /></Link> : null}
      </header>
      {children}
    </section>
  );
}

function MoneyFlow({ rows }) {
  const max = Math.max(...rows.map((row) => Number(row.value) || 0));
  return (
    <div className="flow">
      {rows.map((row) => {
        const value = Number(row.value) || 0;
        return (
          <div key={row.label} className="flow-row">
            <div className="flow-label">
              <span><i className={"dot tone-" + row.tone} />{row.label}</span>
              <strong>{money(value)}</strong>
            </div>
            <div className="flow-track">
              <span className={"flow-bar tone-" + row.tone} style={{ width: max ? (value / max) * 100 + "%" : 0 }} />
            </div>
          </div>
        );
      })}
      {!max ? <p className="hint">No settled volume yet.</p> : null}
    </div>
  );
}

function MemberHealth({ users }) {
  const total = Number(users.total) || 0;
  const active = Number(users.active) || 0;
  const blocked = Number(users.blocked) || 0;
  const other = Math.max(total - active - blocked, 0);
  const activeEnd = total ? (active / total) * 100 : 0;
  const blockedEnd = total ? activeEnd + (blocked / total) * 100 : 0;
  const ring = total
    ? `conic-gradient(var(--chart-1) 0 ${activeEnd}%, var(--chart-2) ${activeEnd}% ${blockedEnd}%, var(--chart-3) ${blockedEnd}% 100%)`
    : "rgba(255, 255, 255, 0.06)";

  return (
    <div className="health">
      <div className="donut" style={{ background: ring }}>
        <div>
          <strong><CountUp value={total} /></strong>
          <span>members</span>
        </div>
      </div>
      <ul className="legend">
        <li><i className="dot tone-c1" />Active<strong>{active}</strong></li>
        <li><i className="dot tone-c2" />Blocked<strong>{blocked}</strong></li>
        {other ? <li><i className="dot tone-c3" />Other<strong>{other}</strong></li> : null}
      </ul>
    </div>
  );
}

function SponsorCode() {
  const { data, loading, error } = useFetch("/sponsor-code");
  const code = data?.data?.referralCode || "";
  const [copied, setCopied] = useState(false);

  function copy() {
    if (!code || !navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <section className="card sponsor-code">
      <label className="field">
        <span>System refer code</span>
        <input readOnly value={loading ? "" : code} placeholder={loading ? "Loading..." : ""} />
      </label>
      <button type="button" className="btn small" disabled={!code} onClick={copy}>
        {copied ? "Copied" : "Copy"}
      </button>
      {error ? <p className="field-error">{error}</p> : null}
    </section>
  );
}

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard() {
  const { data, loading, error } = useFetch("/dashboard");
  const admin = getAdmin() || {};
  const info = data?.data || {};
  const users = info.users || {};
  const deposits = info.deposits || {};
  const withdrawals = info.withdrawals || {};
  const purchases = info.purchases || {};
  const tickets = info.tickets || {};
  const rewards = info.rewards || {};
  const waiting =
    (Number(deposits.pendingCount) || 0) +
    (Number(withdrawals.pendingCount) || 0) +
    (Number(rewards.pendingClaims) || 0) +
    (Number(tickets.openCount) || 0);
  const firstName = String(admin.name || "Admin").split(" ")[0];

  return (
    <Page title="Dashboard" text="Newest members and the queues that still need a decision.">
      <Alerts error={error} />
      <SponsorCode />
      {loading ? <Loading /> : null}
      {data ? <>
      <section className="hero">
        <div className="hero-copy">
          <p className="hero-eyebrow"><span className="live-dot" />{greeting()}, {firstName}</p>
          <h2>
            {waiting ? <><span><CountUp value={waiting} /></span> {waiting === 1 ? "item is" : "items are"} waiting for review</> : "All queues are clear"}
          </h2>
          <p>Deposits, withdrawals, reward claims and support tickets that still need a decision.</p>
          <div className="hero-actions">
            <Link className="btn" to="/admin/deposits?status=pending">Review deposits</Link>
            <Link className="btn ghost" to="/admin/withdrawals?status=pending">Review withdrawals</Link>
          </div>
        </div>
        <div className="hero-figures">
          <div><span>Approved deposits</span><strong>{money(deposits.approvedAmount)}</strong></div>
          <div><span>Package volume</span><strong>{money(purchases.totalAmount)}</strong></div>
          <div><span>Withdrawals paid</span><strong>{money(withdrawals.approvedNet)}</strong></div>
        </div>
      </section>

      <div className="stats">
        <Stat
          to="/admin/members"
          icon="members"
          tone="gold"
          label="Users"
          value={users.total ?? "—"}
          meta={<><span>{users.active ?? 0} active</span><span>{users.blocked ?? 0} blocked</span></>}
        />
        <Stat
          to="/admin/deposits?status=pending"
          icon="deposits"
          tone="good"
          label="Pending deposits"
          value={deposits.pendingCount ?? "—"}
          meta={<span>Approved {money(deposits.approvedAmount)}</span>}
        />
        <Stat
          to="/admin/withdrawals?status=pending"
          icon="withdrawals"
          tone="gold"
          label="Pending withdrawals"
          value={withdrawals.pendingCount ?? "—"}
          meta={<><span>Requested {money(withdrawals.approvedGross)}</span><span>Sent {money(withdrawals.approvedNet)}</span></>}
        />
        <Stat
          to="/admin/purchases?status=active"
          icon="packages"
          tone="violet"
          label="Active packages"
          value={purchases.activeCount ?? "—"}
          meta={<span>All purchases {money(purchases.totalAmount)}</span>}
        />
        <Stat to="/admin/tickets?status=open" icon="support" tone="bad" label="Open tickets" value={tickets.openCount ?? "—"} />
        <Stat to="/admin/reward-claims?status=pending" icon="rewards" tone="teal" label="Pending claims" value={rewards.pendingClaims ?? "—"} />
      </div>

      <div className="dash-grid">
        <Card title="Money flow" text="Settled totals across the platform.">
          <MoneyFlow
            rows={[
              { label: "Approved deposits", value: deposits.approvedAmount, tone: "c1" },
              { label: "Package purchases", value: purchases.totalAmount, tone: "c2" },
              { label: "Withdrawals paid", value: withdrawals.approvedNet, tone: "c3" },
            ]}
          />
        </Card>
        <Card title="Member health" text="Account status across all members." to="/admin/members">
          <MemberHealth users={users} />
        </Card>
      </div>

      <Card title="Newest members" text="The latest sign-ups across your network." to="/admin/members">
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>Email</th><th>Code</th><th>Status</th><th>Joined</th></tr>
            </thead>
            <tbody>
              {(info.recentUsers || []).length ? info.recentUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="name-cell">
                      <Avatar src={user.profileImage} name={user.name} />
                      <Link to={"/admin/members/" + user.id}>{user.name}</Link>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td className="code">{user.referralCode}</td>
                  <td><Badge value={user.status} /></td>
                  <td>{when(user.createdAt)}</td>
                </tr>
              )) : <EmptyRow cols={5} text="No members yet" />}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="split">
        <Card title="Pending deposits" to="/admin/deposits?status=pending">
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Member</th><th className="num">Amount</th><th>Method</th><th>Time</th></tr>
              </thead>
              <tbody>
                {(info.pendingDeposits || []).length ? info.pendingDeposits.map((row) => (
                  <tr key={row.id}>
                    <td><UserLink user={row.user} /></td>
                    <td className="num"><Link to={"/admin/deposits/" + row.id}>{money(row.amount)}</Link></td>
                    <td>{label(row.method)}</td>
                    <td>{when(row.createdAt)}</td>
                  </tr>
                )) : <EmptyRow cols={4} text="No pending deposits" />}
              </tbody>
            </table>
          </div>
        </Card>
        <Card title="Pending withdrawals" to="/admin/withdrawals?status=pending">
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Member</th><th className="num">Pay</th><th>Network</th><th>Address</th></tr>
              </thead>
              <tbody>
                {(info.pendingWithdrawals || []).length ? info.pendingWithdrawals.map((row) => (
                  <tr key={row.id}>
                    <td><UserLink user={row.user} /></td>
                    <td className="num net"><Link to={"/admin/withdrawals/" + row.id}>{money(row.netAmount)}</Link></td>
                    <td>{label(row.network)}</td>
                    <td className="mono">{row.address}</td>
                  </tr>
                )) : <EmptyRow cols={4} text="No pending withdrawals" />}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      </> : null}
    </Page>
  );
}
